import React from 'react';
import formatScalar from './formatValue';

/**
 * One attribute rendered as a badge (05 §1.3, allowlist A35).
 *
 * `render_style: "badge"` is the authored claim that this attribute belongs in
 * the list card's badge slot rather than in a `Label: value` body row. Which
 * slot is DERIVED — there is no `slot:` field to keep in sync with anything —
 * and the whole per-badge vocabulary is the three measured keys of
 * `render_config`:
 *
 *   color         a CSS token (`badge-blue`, `badge-red`, …), or the reserved
 *                 value `accent`, meaning "read the target entry's accent_color
 *                 register" — spell schools and feat categories colour their
 *                 badge from the school/category, not from the spell.
 *   label_prefix  literal text before the value: `CR ` on a challenge rating,
 *                 `Level ` on a feat's minimum level.
 *   when_true     a boolean renders this word instead of "Yes", and renders
 *                 nothing at all when false. `characterclass.prestige` is the
 *                 case: the shipped card shows an orange "Prestige" badge on a
 *                 prestige class and no badge otherwise.
 *
 * `accent` LANDED AT R9, with feats — the first Family whose badge is coloured
 * by the thing it names. The register lives on the TARGET entry, and the list
 * payload does carry it: `serializeLinkTarget` projects the target family's
 * `accent_color`-registered attribute onto every reference row, resolved through
 * `attribute.register` rather than by a hardcoded key, so a family that spells
 * the column differently still colours its badge. What R9 had to add was the
 * other half — the TOKENS. `featcategory.accent_color` is authored
 * `category-color-<slug>` and nothing defined those classes, so an accent badge
 * would have rendered unstyled; `client/src/index.css` now defines them beside
 * the `school-color-*` set they were modelled on.
 *
 * A `many` reference renders ONE BADGE PER TARGET rather than one badge naming
 * them all, and the accent register is the reason: a feat in two categories has
 * two colours, and a joined string can only wear one. That is also why the badge
 * leaf returns a LIST — `badgeItems` — which the list card and the detail
 * header both map over, instead of a string one of them would have to re-split.
 *
 * NUMBER FORMATS LANDED AT R11, with Monsters, and the note this replaces said
 * why they had to arrive here and in `AttrValue` together: `format: "fraction"`
 * is authored on `monster.challenge_rating`, and a badge that did not read it
 * would print `CR 0.5` beside a detail row reading `1/2`. Both leaves call
 * `formatScalar`, which is the one place a number's spelling is decided (G3).
 */

const NEUTRAL = 'badge-gray';
const ACCENT = 'accent';
const ACCENT_REGISTER = 'accent_color';

/** The CSS token one badge wears, given the target it names (or none). */
function tokenFor(attr, target) {
  const color = (attr.renderConfig || {}).color;
  if (color !== ACCENT) return color || NEUTRAL;
  return (target && target[ACCENT_REGISTER]) || NEUTRAL;
}

/**
 * The badges one attribute contributes: `[{ key, text, token }]`, empty when it
 * contributes none. One entry for a scalar, one PER TARGET for a reference.
 */
export function badgeItems(attr, value) {
  const config = attr.renderConfig || {};
  if (attr.datatype === 'boolean') {
    if (!value) return [];
    return [{ key: attr.key, text: config.when_true || attr.label, token: tokenFor(attr, null) }];
  }
  if (attr.kind === 'reference' || attr.kind === 'reverse') {
    const targets = (Array.isArray(value) ? value : [value]).filter((t) => t && t.name);
    return targets.map((target, i) => ({
      key: `${attr.key}:${target.slug || i}`,
      text: `${config.label_prefix || ''}${target.name}`,
      token: tokenFor(attr, target),
    }));
  }
  if (value === undefined || value === null || value === '') return [];
  return [{
    key: attr.key,
    text: `${config.label_prefix || ''}${formatScalar(attr, value)}`,
    token: tokenFor(attr, null),
  }];
}

export default function AttrBadge({ attr, value }) {
  const items = badgeItems(attr, value);
  if (!items.length) return null;
  return (
    <>
      {items.map((item) => (
        <span key={item.key} className={`badge ${item.token}`}>{item.text}</span>
      ))}
    </>
  );
}
